import { useState, useEffect } from 'react';
import { FaBell, FaSpinner, FaCheckCircle, FaExclamationTriangle, FaClock, FaEnvelope, FaUser, FaBookOpen } from 'react-icons/fa';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';

function NotificationManagement() {
  const [stats, setStats] = useState({
    pending: 0,
    notifiedLast30Days: 0,
    failed: 0,
    lastCheckTime: null,
    byType: {
      service: 0,
      trainer: 0,
      program: 0,
    },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isTriggering, setIsTriggering] = useState(false);
  const [lastTriggerResult, setLastTriggerResult] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [filter, setFilter] = useState('pending'); // 'pending' or 'sent'
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      const response = await HttpInterceptor.get('/api/trainer-availability-notifications/stats');
      if (response?.data?.data) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching notification stats:', error);
      toast.error('Failed to load notification statistics');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotifications = async (page = 1, isNotified = 'false') => {
    try {
      setNotifLoading(true);
      const response = await HttpInterceptor.get(`/api/trainer-availability-notifications/all`, {
        params: {
          page,
          limit: pagination.limit,
          isNotified: isNotified === 'sent' ? 'true' : 'false'
        }
      });
      if (response?.data?.data) {
        setNotifications(response.data.data.notifications || []);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notification list');
    } finally {
      setNotifLoading(false);
    }
  };

  const handleTriggerCheck = async () => {
    try {
      setIsTriggering(true);
      setLastTriggerResult(null);
      
      const response = await HttpInterceptor.post('/api/trainer-availability-notifications/check');
      
      if (response?.data?.data) {
        const result = response.data.data;
        setLastTriggerResult(result);
        
        if (result.sent > 0) {
          toast.success(`Successfully sent ${result.sent} notification${result.sent !== 1 ? 's' : ''}`);
        } else {
          toast.info('No notifications to send at this time');
        }
        
        if (result.failed > 0) {
          toast.warning(`${result.failed} notification${result.failed !== 1 ? 's' : ''} failed to send`);
        }
        
        // Refresh stats and notifications after triggering
        setTimeout(() => {
          fetchStats();
          fetchNotifications(1, filter);
        }, 1000);
      }
    } catch (error) {
      console.error('Error triggering notification check:', error);
      toast.error('Failed to trigger notification check');
    } finally {
      setIsTriggering(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchNotifications(1, filter);
  }, [filter]);

  const handlePageChange = (newPage) => {
    fetchNotifications(newPage, filter);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Notification Management</h1>
          <p className="text-gray-600">Monitor and manage trainer availability notifications</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Pending Notifications */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-2">Pending Notifications</p>
                {isLoading ? (
                  <FaSpinner className="animate-spin text-2xl text-gray-400" />
                ) : (
                  <p className="text-3xl font-bold text-gray-900">{stats.pending}</p>
                )}
              </div>
              <div className="bg-yellow-100 text-yellow-600 p-4 rounded-full">
                <FaBell className="text-2xl" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-600">
                <FaClock className="mr-2" />
                <span>Awaiting availability</span>
              </div>
            </div>
          </div>

          {/* Notified Last 30 Days */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-2">Sent (Last 30 Days)</p>
                {isLoading ? (
                  <FaSpinner className="animate-spin text-2xl text-gray-400" />
                ) : (
                  <p className="text-3xl font-bold text-gray-900">{stats.notifiedLast30Days}</p>
                )}
              </div>
              <div className="bg-green-100 text-green-600 p-4 rounded-full">
                <FaCheckCircle className="text-2xl" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-600">
                <FaEnvelope className="mr-2" />
                <span>Emails sent</span>
              </div>
            </div>
          </div>

          {/* Failed Notifications */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-2">Failed</p>
                {isLoading ? (
                  <FaSpinner className="animate-spin text-2xl text-gray-400" />
                ) : (
                  <p className="text-3xl font-bold text-gray-900">{stats.failed}</p>
                )}
              </div>
              <div className="bg-red-100 text-red-600 p-4 rounded-full">
                <FaExclamationTriangle className="text-2xl" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-600">
                <FaExclamationTriangle className="mr-2" />
                <span>Send errors</span>
              </div>
            </div>
          </div>

          {/* Last Check Time */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-2">Last Check</p>
                {isLoading ? (
                  <FaSpinner className="animate-spin text-2xl text-gray-400" />
                ) : (
                  <p className="text-sm font-semibold text-gray-900">
                    {getTimeAgo(stats.lastCheckTime)}
                  </p>
                )}
              </div>
              <div className="bg-blue-100 text-blue-600 p-4 rounded-full">
                <FaClock className="text-2xl" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-xs text-gray-500">
                {formatDate(stats.lastCheckTime)}
              </div>
            </div>
          </div>
        </div>

        {/* Notification List Section */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <h2 className="text-xl font-bold text-gray-900">Notification History</h2>
            <div className="flex bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setFilter('pending')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  filter === 'pending' ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => setFilter('sent')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  filter === 'sent' ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Sent
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {notifLoading ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center">
                      <FaSpinner className="animate-spin text-primary text-3xl mx-auto" />
                      <p className="mt-2 text-gray-500">Loading notifications...</p>
                    </td>
                  </tr>
                ) : notifications.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                      No {filter} notifications found.
                    </td>
                  </tr>
                ) : (
                  notifications.map((notif) => (
                    <tr key={notif._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-500">
                            <FaUser />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {notif.userId?.name || 'Unauthenticated User'}
                            </div>
                            <div className="text-sm text-gray-500">{notif.email}</div>
                            {notif.userId?.mobile && <div className="text-xs text-gray-400">{notif.userId.mobile}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {notif.programId ? (
                          <div className="flex flex-col">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 self-start mb-1">
                              Program
                            </span>
                            <span className="text-sm font-semibold">{notif.programId.name}</span>
                            <span className="text-xs text-gray-500">by {notif.trainerId?.user?.name || 'Trainer'}</span>
                          </div>
                        ) : notif.trainerId ? (
                          <div className="flex flex-col">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 self-start mb-1">
                              Trainer
                            </span>
                            <span className="text-sm font-semibold">{notif.trainerId?.user?.name || 'Trainer'}</span>
                          </div>
                        ) : (
                          <div className="flex flex-col">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 self-start mb-1">
                              Service
                            </span>
                            <span className="text-sm font-semibold">{notif.service}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(notif.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {notif.isNotified ? (
                          <div className="flex flex-col">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 self-start">
                              Notified
                            </span>
                            <span className="text-xs text-gray-400 mt-1">{formatDate(notif.notifiedAt)}</span>
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing page <span className="font-medium">{pagination.page}</span> of <span className="font-medium">{pagination.pages}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Notification Types Breakdown */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Pending by Type</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center p-4 bg-blue-50 rounded-lg">
              <FaBookOpen className="text-blue-600 text-2xl mr-4" />
              <div>
                <p className="text-sm text-gray-600">Service Level</p>
                <p className="text-2xl font-bold text-gray-900">{stats.byType.service}</p>
              </div>
            </div>
            <div className="flex items-center p-4 bg-green-50 rounded-lg">
              <FaUser className="text-green-600 text-2xl mr-4" />
              <div>
                <p className="text-sm text-gray-600">Trainer Level</p>
                <p className="text-2xl font-bold text-gray-900">{stats.byType.trainer}</p>
              </div>
            </div>
            <div className="flex items-center p-4 bg-purple-50 rounded-lg">
              <FaBookOpen className="text-purple-600 text-2xl mr-4" />
              <div>
                <p className="text-sm text-gray-600">Program Level</p>
                <p className="text-2xl font-bold text-gray-900">{stats.byType.program}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Manual Trigger Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Manual Notification Check</h2>
              <p className="text-sm text-gray-600 mt-1">
                Manually trigger a check for all pending notifications
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Notifications are automatically triggered when trainers create or activate programs. 
              Use this button to manually check for any missed notifications or to test the system.
            </p>
          </div>

          <button
            onClick={handleTriggerCheck}
            disabled={isTriggering || isLoading}
            className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isTriggering ? (
              <>
                <FaSpinner className="animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <FaBell />
                Trigger Notification Check
              </>
            )}
          </button>

          {lastTriggerResult && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-semibold text-gray-900 mb-2">Last Check Result:</p>
              <div className="flex gap-4 text-sm">
                <div className="flex items-center text-green-600">
                  <FaCheckCircle className="mr-2" />
                  <span>Sent: {lastTriggerResult.sent}</span>
                </div>
                {lastTriggerResult.failed > 0 && (
                  <div className="flex items-center text-red-600">
                    <FaExclamationTriangle className="mr-2" />
                    <span>Failed: {lastTriggerResult.failed}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default NotificationManagement;

